/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Wire types for the Migration Dashboard (api-insights src/api/app/admin/migration/**).
 */

export const MIGRATION_STATUSES = ['todo', 'ready', 'confirmed', 'rejected'] as const
export type MigrationStatus = (typeof MIGRATION_STATUSES)[number]

export interface MigrationNeeds {
  company: boolean
  asset: boolean
  dates: boolean
}

/** A recorded run — GET …/items/:id/runs, and `lastRun` on each item. */
export interface MigrationRun {
  id: number
  /** `test`, or `sql:<pair id>`. */
  target: string
  ranAt: string | Date
  ranBy: string | null
  companyId: number
  assetId: number | null
  /** Wall-clock (apiService may hand these over as Dates). */
  from: string | Date
  to: string | Date
  timezone: string | null
  coordDigits: number | null
  numberDigits: number | null
  /** null when a side failed. */
  match: boolean | null
  freshCount: number
  timescaleCount: number
  freshError: string | null
  timescaleError: string | null
  summary: { counts?: Partial<Record<RowStatus, number>>; fieldMismatches?: Record<string, number> }
}

/** GET /app/admin/migration/items */
export interface MigrationItem {
  id: string
  kind: 'endpoint' | 'issue'
  name: string
  description: string | null
  path: string | null
  freshPath: string | null
  ticket: string | null
  converted: boolean | null
  needs: MigrationNeeds
  tickets: string[]
  status: MigrationStatus
  jiraKey: string | null
  notes: string | null
  updatedBy: string | null
  updatedAt: string | Date | null
  sqlCount: number
  lastRun: MigrationRun | null
}

export interface CompareSpec {
  key?: string[]
  fields?: string[]
  ignore?: string[]
  numericTolerance?: number
  timeToleranceSec?: number
  knownGaps?: Record<string, string>
}

export interface SqlPair {
  id: string
  title: string
  explains: string
  needsAsset: boolean
  fresh: string
  timescale: string
  compare: CompareSpec
}

/** GET /app/admin/migration/items/:id */
export interface MigrationItemDetail extends MigrationItem {
  compare: CompareSpec | null
  sql: SqlPair[]
}

/** GET /app/admin/migration/defaults */
export interface MigrationDefaults {
  companyId: number
  /** Wall-clock on the wire; apiService hands it over as a Date. */
  from: string | Date
  to: string | Date
  timezone: string
  asset: { assetId: number; name: string; locationSamples: number; temperatureSamples: number } | null
}

export interface MigrationFilters {
  companyId?: number
  assetId?: number
  from?: string
  to?: string
  /** Decimals latitude/longitude are rounded to before comparing. */
  coordDigits: number
  /** Decimals every other number is rounded to before comparing. */
  numberDigits: number
}

export const DEFAULT_COORD_DIGITS = 4
export const DEFAULT_NUMBER_DIGITS = 1

export type Row = Record<string, unknown>
export type RowStatus = 'match' | 'mismatch' | 'shifted' | 'onlyFresh' | 'onlyTimescale' | 'duplicate'

export interface CompareRow {
  key: string
  status: RowStatus
  fresh?: Row
  timescale?: Row
  diffFields: string[]
  gapFields: string[]
}

export interface CompareResult {
  match: boolean
  freshCount: number
  timescaleCount: number
  counts: Record<RowStatus, number>
  fieldMismatches: Record<string, number>
  knownGaps: Record<string, { ticket: string; rows: number }>
  rows: CompareRow[]
}

export interface SideResult {
  ok: boolean
  ms: number
  count: number
  truncated: boolean
  status?: number
  error?: string
  url?: string
}

/** POST …/items/:id/test and …/items/:id/sql/:sqlId */
export interface RunResult {
  /** The recorded run; null if recording failed. */
  run: MigrationRun | null
  fresh: SideResult
  timescale: SideResult
  compare: CompareResult | null
}

/** What was run: the endpoint test, or one of the item's SQL pairs. */
export type RunTarget = { kind: 'test' } | { kind: 'sql'; sqlId: string }

export const JIRA_BASE = 'https://cooltrax.atlassian.net/browse/'
